import { logger } from '@main/core-ui';
import React, { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { inlineConfirmMessages } from './messages';
import { ConfirmText, ErrorWrapper, StyledButton } from './wrappers';

/**
 * Props for the InlineConfirm component
 */
export interface InlineConfirmProps {
  /** The message text to show for confirmation */
  confirmationMessage: MessageDescriptor;
  /** The main button message */
  button: MessageDescriptor;
  /** The no button message alternative */
  noButton?: MessageDescriptor;
  /** The yes button message alternative */
  yesButton?: MessageDescriptor;
  /** The function to fire on confirm */
  onConfirm: () => Promise<void>;
  /** Whether the action is loading */
  loading?: boolean;
  /** Error message, if applicable */
  error?: string;
}

/**
 * An inline confirmation component to cancel or confirm an action
 */
export const InlineConfirm: React.FC<InlineConfirmProps> = ({
  confirmationMessage,
  button,
  noButton = inlineConfirmMessages.no,
  yesButton = inlineConfirmMessages.yes,
  onConfirm,
  loading,
  error,
}) => {
  const { formatMessage } = useIntl();
  const [cancelling, setCancelling] = useState(false);

  const textContents =
    error && !cancelling ? (
      <ErrorWrapper>{error}</ErrorWrapper>
    ) : (
      <ConfirmText visible={cancelling}>
        {formatMessage(confirmationMessage)}
      </ConfirmText>
    );

  const interactiveContents = cancelling ? (
    <div>
      <StyledButton
        buttonType="secondary"
        onClick={() => setCancelling(false)}
        disabled={loading}
      >
        {formatMessage(noButton)}
      </StyledButton>
      <StyledButton
        buttonType="primary"
        loading={loading}
        onClick={() => {
          onConfirm()
            .then(() => setCancelling(false))
            .catch((err) => {
              logger.warn(err);
              setCancelling(false);
            });
        }}
        disabled={loading}
      >
        {formatMessage(yesButton)}
      </StyledButton>
    </div>
  ) : (
    <StyledButton
      buttonType="secondary"
      loading={loading}
      onClick={() => setCancelling(true)}
    >
      {formatMessage(button)}
    </StyledButton>
  );

  return (
    <div>
      {textContents}
      {interactiveContents}
    </div>
  );
};
